/**
 * Todo Types
 * 
 * Matches D1 schema from cortex-api/migrations/003_create_user_todos.sql
 * For offline mode, we'll use these types and sync to D1 later.
 */

export interface Todo {
  id: string;
  user_id?: string; // Optional for offline mode
  title: string;
  description?: string;
  due_date?: number; // Unix timestamp (milliseconds)
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in_progress' | 'completed';
  category?: string;
  parent_id?: string; // For subtasks
  decision_id?: string;
  schedule_id?: string;
  created_at: number;
  updated_at: number;
  completed_at?: number;
  metadata?: string; // JSON string
}

/**
 * Create a new todo (for offline mode)
 */
export function createTodo(
  title: string,
  options: {
    description?: string;
    due_date?: number;
    priority?: 'low' | 'medium' | 'high';
    category?: string;
    parent_id?: string;
  } = {}
): Todo {
  const now = Date.now();
  return {
    id: `todo_${now}_${Math.random().toString(36).substr(2, 9)}`,
    title,
    description: options.description,
    due_date: options.due_date,
    priority: options.priority || 'medium',
    status: 'pending',
    category: options.category,
    parent_id: options.parent_id,
    created_at: now,
    updated_at: now
  };
}

/**
 * Update a todo
 */
export function updateTodo(
  todo: Todo,
  updates: Partial<Omit<Todo, 'id' | 'created_at'>>
): Todo {
  return {
    ...todo,
    ...updates,
    updated_at: Date.now()
  };
}

/**
 * Mark todo as completed
 */
export function completeTodo(todo: Todo): Todo {
  return updateTodo(todo, {
    status: 'completed',
    completed_at: Date.now()
  });
}

/**
 * Mark todo as pending (uncomplete)
 */
export function uncompleteTodo(todo: Todo): Todo {
  return updateTodo(todo, {
    status: 'pending',
    completed_at: undefined
  });
}

/**
 * Delete a todo (returns new array without the todo)
 */
export function deleteTodo(todos: Todo[], todoId: string): Todo[] {
  return todos.filter(t => t.id !== todoId);
}

